// inboxStore — Phase 2c. Filter + snooze + ETA-banner UI state for the Coach Inbox.
import { create } from 'zustand';

export type InboxFilter = 'unread' | 'all' | 'voice' | 'archived' | 'snoozed';

const ETA_KEY = 'inbox:etaBannerDismissed';
const readEtaDismissed = () => {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem(ETA_KEY) === '1'; } catch { return false; }
};

interface InboxState {
  activeFilter: InboxFilter;
  threadFocusActive: boolean;
  snoozedThreadIds: Set<string>;
  // Phase 2c.3: threads whose resurfaced badge the coach has dismissed this
  // session (optimistic — server confirms via inbox:resurfaced-dismissed). Lets
  // the badge vanish the instant a thread is opened, before the refetch lands.
  resurfacedDismissedThreadIds: Set<string>;
  etaBannerDismissed: boolean;
  setFilter: (f: InboxFilter) => void;
  setThreadFocusActive: (active: boolean) => void;
  snoozeThread: (id: string) => void;
  unsnoozeThread: (id: string) => void;
  isSnoozed: (id: string) => boolean;
  markResurfacedDismissed: (id: string) => void;
  clearResurfaced: () => void;
  dismissEtaBanner: () => void;
}

export const useInboxStore = create<InboxState>((set, get) => ({
  // Default to 'all' to preserve the legacy landing (every non-archived thread,
  // urgency-sorted). Coaches opt into triage views via the chips.
  activeFilter: 'all',
  threadFocusActive: false,
  snoozedThreadIds: new Set<string>(),
  resurfacedDismissedThreadIds: new Set<string>(),
  etaBannerDismissed: readEtaDismissed(),
  setFilter: (f) => set({ activeFilter: f }),
  setThreadFocusActive: (active) => set({ threadFocusActive: active }),
  snoozeThread: (id) => set((s) => { const n = new Set(s.snoozedThreadIds); n.add(id); return { snoozedThreadIds: n }; }),
  unsnoozeThread: (id) => set((s) => { const n = new Set(s.snoozedThreadIds); n.delete(id); return { snoozedThreadIds: n }; }),
  isSnoozed: (id) => get().snoozedThreadIds.has(id),
  markResurfacedDismissed: (id) => set((s) => { const n = new Set(s.resurfacedDismissedThreadIds); n.add(id); return { resurfacedDismissedThreadIds: n }; }),
  clearResurfaced: () => set({ resurfacedDismissedThreadIds: new Set<string>() }),
  dismissEtaBanner: () => { try { localStorage.setItem(ETA_KEY, '1'); } catch { /* private mode */ } set({ etaBannerDismissed: true }); },
}));
